"use client";

import {
  ChevronRightIcon,
  MinusIcon,
  PlayIcon,
} from "@heroicons/react/24/solid";
import Image from "next/image";
import YouTube from "react-youtube";

import { handleCoverSelect } from "@/components/fileCoverInput/inputMethods/coverUpload/selectCoverModal/handleCoverSelect";
import { NormalText } from "@/components/text/NormalText";
import { SoftText } from "@/components/text/SoftText";
import { useSetAuthModalOpen } from "@/lib/clerk/useSetAuthModalOpen";
import { AudioInputState, Cover, UserNoDates } from "@/types/types";
import { trackLengthFormat } from "@/utils/formatTime/trackLengthFormat";

const opts: Record<string, unknown> = {
  height: "100%",
  width: "100%",
  playerVars: {
    // https://developers.google.com/youtube/player_parameters
    autoplay: 1,
    rel: 0,
  },
};

export function CoverSearchItem({
  cover,
  openCoverId,
  setOpenCoverId,
  closeModal,
  user,
  checkSubscription,
  setInvalidLengthSubscriptionModalOpen,
  audioLengthMinLimitMinutes,
  audioLengthMaxLimitMinutes,
  setInvalidLengthMaxModalOpen,
  showCover,
  setInputCover,
}: {
  cover: Cover;
  openCoverId: Cover["id"] | null;
  setOpenCoverId: (id: Cover["id"] | null) => void;
  closeModal: () => void;
  user: UserNoDates | null;
  checkSubscription: boolean;
  setInvalidLengthSubscriptionModalOpen: (value: boolean) => void;
  audioLengthMinLimitMinutes: number | null;
  audioLengthMaxLimitMinutes: number;
  setInvalidLengthMaxModalOpen: (value: boolean) => void;
  showCover: boolean;
  setInputCover: AudioInputState["setInputCover"];
}) {
  const setAuthModalOpen = useSetAuthModalOpen();

  const coverOpen = openCoverId === cover.id;

  return (
    <li>
      <div className="flex w-full items-stretch">
        <button
          onClick={() => {
            if (coverOpen) {
              setOpenCoverId(null);
            } else {
              setOpenCoverId(cover.id);
            }
          }}
          className="px-2 py-2 sm:px-4"
        >
          {coverOpen ?
            <MinusIcon className="h-6 w-6 shrink-0 fill-gray-900" />
          : <PlayIcon className="h-6 w-6 shrink-0 fill-gray-900" />}
        </button>
        <button
          data-testid={`select-cover-option-${cover.id}`}
          className="flex grow py-2"
          onClick={() => {
            handleCoverSelect(
              cover,
              user,
              setAuthModalOpen,
              checkSubscription,
              setInvalidLengthSubscriptionModalOpen,
              audioLengthMinLimitMinutes,
              audioLengthMaxLimitMinutes,
              setInvalidLengthMaxModalOpen,
              setInputCover,
              showCover
            );
            closeModal();
          }}
        >
          <div className="flex h-full items-center">
            <div className="flex h-12 w-12 overflow-hidden rounded-xl sm:h-14 sm:w-14 sm:rounded-2xl">
              <Image
                src={cover.thumbnailUrl}
                alt={cover.title}
                width={100}
                height={100}
                className="-mt-[18.5%] h-[137%] object-cover"
                unoptimized
              />
            </div>
          </div>

          <div className="grow pl-2 sm:pl-4">
            <NormalText
              elementType="p"
              className="mb-1 text-left text-gray-900"
            >
              {cover.title}
            </NormalText>
            <SoftText elementType="p" className="text-left font-normal">
              {trackLengthFormat(cover.audioLength)}
            </SoftText>
          </div>

          <div className="flex h-full items-center px-2 sm:px-4">
            <ChevronRightIcon
              className="h-7 w-7 flex-none text-gray-400"
              aria-hidden="true"
            />
          </div>
        </button>
      </div>
      {coverOpen && (
        <div className="flex justify-center p-5">
          <div className="w-full max-w-sm">
            <div className="aspect-h-9 aspect-w-16 relative">
              <YouTube videoId={cover.id} opts={opts} />
            </div>
          </div>
        </div>
      )}
    </li>
  );
}
